import { API_BASE_URL, fetchWithAuth, getActiveBusinessId, getDefaultHeaders, getServerUrlWithMatchingProtocol } from '../config/api';

/**
 * The owner's end of the business listing — what `/my-listing` reads and writes.
 *
 * Two shapes travel over these routes and they are NOT the same shape, deliberately:
 *
 * - The OWNER shape ({@link BusinessListingDto}) is what is STORED. It carries the raw
 *   `discountPercent` and `discountEndsOn`, because the editor edits stored values and showing it
 *   a resolved price it cannot type back would be a lie.
 * - The PUBLIC shape ({@link PublicListing}) is what a visitor receives. Discounts are already
 *   resolved against today — `discountedCents` is computed, an expired discount is simply absent,
 *   and there is no `discountEndsOn` at all, because a visitor needs to know whether an offer
 *   applies now, not what our records say about when it stops.
 *
 * The public types live here rather than in `marketplaceApi` so the two halves of one payload are
 * defined together and cannot drift apart; the marketplace business DTO names `PublicListing` from
 * here.
 */

const LISTING_URL = `${API_BASE_URL}/business-listing/me`;

/** Note there is no `id`. The API is entirely business-scoped and the server sends none. */
export interface BusinessListingDto {
    headline: string;
    heroImageId: string | null;
    status: 'draft' | 'published';
    /** First publish, retained through later edits — it dates the listing, not the edit. */
    publishedAt: string | null;
    updatedAt: string;
    items: ListingItemDto[];
}

export interface ListingItemDto {
    /** Eight hex characters, minted server-side. Unique within the listing. */
    id: string;
    imageId: string | null;
    title: string;
    description: string;
    /** Integer cents, never a dollar amount. Null means the item shows no price. */
    priceCents: number | null;
    priceIsFrom: boolean;
    priceUnit: string;
    discountPercent: number | null;
    /** Date-only, inclusive of the day it names. Null means the discount does not expire. */
    discountEndsOn: string | null;
}

/** What `PUT me` accepts. No `status`: publishing is its own action, so saving never publishes. */
export interface SaveListingRequest {
    headline: string;
    heroImageId: string | null;
    items: ListingItemDto[];
}

/** Mirrors `PublicListingDto`. No status, no `updatedAt`, no `updatedByUserId`. */
export interface PublicListing {
    headline: string;
    heroImageId: string | null;
    publishedAt: string | null;
    items: PublicListingItem[];
}

/**
 * Mirrors `PublicListingItemDto`. `discountPercent` is null unless the discount applies TODAY, and
 * `discountedCents` is the price already worked out — the client is never asked to decide either.
 */
export interface PublicListingItem {
    id: string;
    imageId: string | null;
    title: string;
    description: string;
    priceCents: number | null;
    priceIsFrom: boolean;
    priceUnit: string;
    discountPercent: number | null;
    discountedCents: number | null;
}

/**
 * Where a listing picture is served from.
 *
 * The route is public and unauthenticated because the page it belongs to is — but it serves ONLY
 * for a business whose listing is PUBLISHED. That is worth knowing in the editor: a picture on a
 * draft listing will not load from this URL until the listing is published, so the editor previews
 * a fresh upload from the local file instead of round-tripping it.
 */
export function listingImageUrl(businessId: string, imageId: string): string {
    return `${API_BASE_URL}/public/business-listing/${encodeURIComponent(businessId)}/image/${encodeURIComponent(imageId)}`;
}

export const businessListingApi = {
    /** The caller's listing. The server hands back an empty draft rather than 404ing on first open. */
    get: (): Promise<BusinessListingDto> =>
        fetchWithAuth<BusinessListingDto>(LISTING_URL, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    /** A whole-document replace, so a reorder is one save rather than a choreography of moves. */
    save: (listing: SaveListingRequest): Promise<BusinessListingDto> =>
        fetchWithAuth<BusinessListingDto>(LISTING_URL, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify(listing),
        }),

    publish: (): Promise<BusinessListingDto> =>
        fetchWithAuth<BusinessListingDto>(`${LISTING_URL}/publish`, {
            method: 'POST',
            headers: getDefaultHeaders(),
        }),

    unpublish: (): Promise<BusinessListingDto> =>
        fetchWithAuth<BusinessListingDto>(`${LISTING_URL}/unpublish`, {
            method: 'POST',
            headers: getDefaultHeaders(),
        }),

    /**
     * Stores a picture and returns the id that names it.
     *
     * A bare `fetch` rather than `fetchWithAuth`: that helper sets a JSON Content-Type, and a
     * multipart body's content type has to carry the boundary the BROWSER generates. Setting it by
     * hand produces a request the server cannot parse at all, so the headers are built explicitly
     * here rather than taken from `getDefaultHeaders()` and edited. Everything else about the auth
     * is a faithful copy of what `fetchWithAuth` does — cookies via `credentials: 'include'`, plus
     * X-Business-Id, which is what decides WHICH business the picture is stored under for a user
     * who has more than one.
     *
     * The id is not saved anywhere by this call. The caller puts it on the listing and saves in the
     * ordinary way, so a picture chosen and then abandoned changes nothing.
     */
    uploadImage: async (file: File): Promise<string> => {
        const body = new FormData();
        body.append('file', file);

        const headers: Record<string, string> = {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };
        const businessId = getActiveBusinessId();
        if (businessId) headers['X-Business-Id'] = businessId;

        const url = `${LISTING_URL}/images`;
        const response = await fetch(
            url.startsWith('http') ? url : `${getServerUrlWithMatchingProtocol()}/${url.replace(/^\/+/, '')}`,
            { method: 'POST', headers, body, credentials: 'include' },
        );

        if (!response.ok) {
            // The server's own words where it gave any — "That picture is larger than 2MB" tells
            // someone what to do next, where a generic failure leaves them guessing.
            const problem = await response.json().catch(() => null) as { message?: string } | null;
            throw new Error(problem?.message || 'Could not upload that picture');
        }

        const { imageId } = await response.json() as { imageId: string };
        return imageId;
    },
};
